<template>
  <div>
    <div
      v-if="quiz"
      class="quiz-wrapper pos-rel d-flex justify-center"
    >
      <div
        v-if="quiz.accentShapes === 'bars'"
        class="pos-abs orange-bar"
      />
      <div class="quiz-content-wrapper">
        <div
          v-if="quizProgress === QuizProgress.NotStarted"
          class="d-flex flex-column justify-center align-center text-center"
        >
          <span aria-hidden="true">
            <i
              v-if="siteConfig"
              class="quiz-icon mdi mdi-help-circle-outline"
              :class="(siteConfig.navBottomBorderColor === 'orange') ? 'gold--text' : 'accent--text'"
            />
          </span>
          <h2 class="secondary--text h1">
            {{ quiz.heading }}
          </h2>
          <ctf-rich-text class="mt-6" :document="quiz.subHeading" />
          <base-button
            class="mt-6"
            text="Take the quiz"
            @click="setQuizProgress(QuizProgress.Active)"
          />
        </div>
        <div
          v-else-if="quizProgress === QuizProgress.Active"
          class="d-flex flex-column justify-center"
        >
          <quiz-question :index="activeQuestion" :fields="quiz.questions[activeQuestion].fields" />
        </div>
        <div
          v-else-if="quizProgress === QuizProgress.Completed && result"
          class="d-flex flex-column justify-center align-center text-center"
          style="max-width: 570px"
        >
          <i class="quiz-icon mdi mdi-checkbox-marked-circle-outline accent--text" />
          <h2 id="quiz-result-heading" class="secondary--text h1">
            {{ result.fields.heading }}
          </h2>
          <ctf-rich-text class="mt-6" :document="result.fields.subHeading" />
        </div>
        <div
          v-if="quizProgress !== QuizProgress.NotStarted"
          class="d-flex flex-column justify-center"
        >
          <div
            class="d-flex flex-column align-center"
            style="margin-top: 32px;"
          >
          <v-img
                v-if="quizProgress === QuizProgress.Completed && result.fields.buttonImage.asset && (result && result.fields.buttonImage)"
                :src="result.fields.buttonImage.asset.fields.file.url"
                :alt="result.fields.buttonImage.asset.fields.description || 'Page Slice Asset'"
                class="align-center button-image"
                max-width="290"
                min-width="100"
                aspect-ratio="1.7778"
                width="100%"
                :role="undefined"
                :aria-label="false"
                />
            <ctf-link
              v-if="quizProgress === QuizProgress.Completed && (result && result.fields.action)"
              :btn-id="result.fields.action.sys.id"
              class="mb-4"
              ref="linkButton"
            />
            <div class="d-flex justify-center align-center mb-4">
              <base-button
                v-if="quizProgress !== QuizProgress.Completed"
                class="quiz-icon-btn mr-3"
                icon="mdi-chevron-left"
                :disabled="!(activeQuestion > 0)"
                aria-label="previous question"
                @click="navigateQuiz(false)"
              />
              <span
                v-if="quizProgress !== QuizProgress.Completed"
                class="mx-3 font-default-size font-weight-bold primary--text"
              >
                Question {{ activeQuestion + 1 }} of {{ quiz.questions.length }}
              </span>
              <span
                v-else
                class="font-default-size font-weight-bold primary--text text-uppercase"
              >
                Complete
              </span>
              <base-button
                v-if="quizProgress !== QuizProgress.Completed"
                class="quiz-icon-btn ml-3"
                icon="mdi-chevron-right"
                :disabled="!selected"
                aria-label="next question"
                @click="navigateQuiz"
              />
            </div>
            <v-progress-linear
              color="primary"
              background-color="gray"
              class="mx-auto"
              height="8"
              style="max-width: 450px; width: 100%"
              :value="(activeQuestion + 1) / quiz.questions.length * 100"
              rounded
              aria-label="Quiz progress bar"
              :aria-busy="false"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="(activeQuestion + 1) / quiz.questions.length * 100"
            />
            <button
              v-if="quizProgress === QuizProgress.Completed"
              class="mt-8 info--text"
              @click="resetQuiz"
            >
              Retake Quiz
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="quiz.accentShapes === 'bars'"
        class="pos-abs green-bar"
      />
      <div
        v-if="quiz.accentShapes === 'bars'"
        class="pos-abs orange-bar--large"
      />
      <div
        v-if="quiz.accentShapes !== 'bars'"
        class="pos-abs blue-green-triangles"
      />
    </div>
    <slices-block
      v-if="quizProgress === QuizProgress.Completed && result"
      :entries="result.fields.slices"
      :assets="assets"
    />
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions
} from 'vuex'
import { QUIZ_TYPES, QuizProgress } from '@/store/modules/quiz'
import { queryCTF, sortCTFEntries, getCTFAsset } from '@/plugins/contentful'
import QuizQuestion from '@/components/slices/quiz/quiz-question'
import CtfLink from '@/components/snippets/ctf-link'
import PixelTracker from '@/components/base/pixel-tracker'

export default {
  name: 'QuizSlice',
  components: {
    SlicesBlock: () => import('@/components/slices/slices-block'),
    QuizQuestion,
    CtfLink
  },
  props: {
    sysId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      QuizProgress,
      assets: null
    }
  },
  computed: {
    ...mapState({ siteConfig: state => state.site.siteConfig }),
    ...mapState('quiz', [
      'quiz',
      'quizProgress',
      'activeQuestion',
      'selections',
      'result'
    ]),
    ...mapGetters({
      selected: QUIZ_TYPES.getters.selected
    })
  },
  async created () {
    const { items, includes } = await queryCTF(`&include=2&content_type=quiz&select=fields&sys.id=${this.sysId}`)
    if (includes.Asset) {
      this.assets = includes.Asset
    }
    if (items && includes) {
      const quiz = items[0].fields
      if (includes.Entry) {
        quiz.questions = sortCTFEntries(quiz.questions, includes.Entry)
        quiz.results = sortCTFEntries(quiz.results, includes.Entry)
        if (quiz.results) {
          quiz.results.forEach(r => {
            r.fields.slices = sortCTFEntries(r.fields.slices, includes.Entry)
            if (r.fields.buttonImage) {
              r.fields.buttonImage.asset = getCTFAsset(r.fields.buttonImage.sys.id, this.assets)
            }
          })
        }
      }
      this.setQuiz(quiz)
    }
  },
  methods: {
    ...mapMutations({
      setQuiz: QUIZ_TYPES.mutations.SET_QUIZ,
      setQuizProgress: QUIZ_TYPES.mutations.SET_QUIZ_PROGRESS,
      setActiveQuestion: QUIZ_TYPES.mutations.SET_ACTIVE_QUESTION
    }),
    ...mapActions({
      resetQuiz: QUIZ_TYPES.actions.resetQuiz,
      getResult: QUIZ_TYPES.actions.getResult
    }),
    navigateQuiz (advance = true) {
      if (this.$analytics) {
        this.$analytics.track('Quiz_Question_' + this.activeQuestion)
      }
      if (advance) {
        if (this.activeQuestion + 1 > this.quiz.questions.length) {
          return
        } else if (this.activeQuestion + 1 === this.quiz.questions.length) {
          this.getResults()
          return
        }
        this.setActiveQuestion(this.activeQuestion + 1)
      } else {
        if (this.activeQuestion - 1 < 0) {
          return
        }
        this.setActiveQuestion(this.activeQuestion - 1)
      }
    },
    getResults () {
      if (this.$analytics) {
        this.$analytics.track('Quiz_Results')
      }
      this.siteConfig.careNetworkName.indexOf('Vermont') !== -1 ? PixelTracker.track('601117795', 'ACINCOH_hdwBEOOo0Z4C') : PixelTracker.track('608189871', '1eAJCNG2xtkBEK_7gKIC')

      this.getResult()
      this.setQuizProgress(QuizProgress.Completed)
    },
    imageClick () {
      if (this.$refs.linkButton) {
        this.$refs.linkButton.handleClick()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import 'src/styles/variables';
  .quiz-wrapper {
    margin: 78px 0;
  }
  .quiz-content-wrapper {
    margin: 76px 0 174px 0;
  }
  .quiz-icon {
    font-size: 75px;
  }
  .quiz-icon-btn {
    max-width: 60px !important;
    min-width: initial !important;
  }
  .button-image {
    margin-bottom: 45px;
  }
  .green-bar {
    left: 0;
    bottom: 47px;
    margin-left: -5%; // 5% to counter-act content-constraint
    width: 42%;
    height: 8px;
    border-radius: 100px;
    background-color: $neon-green;
  }
  .orange-bar {
    right: 0;
    margin-right: 0%; // aligns directly on the right side
    width: 17%;
    height: 8px;
    border-radius: 100px 0 0 100px;
    background-color: $yellow;
  }
  .orange-bar--large {
    @extend .orange-bar;
    width: 79%;
    bottom: 0;
  }
  .blue-green-triangles {
    max-height: 220px;
    width: 100%;
    height: 100%;
    left: 0;
    bottom: 0;
    z-index: -1;
    margin-bottom: -80px;
    background-image: url("/assets/img/blue-green-triangles.jpg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }
</style>
